import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, IconButton, FormControl, InputLabel, Select, MenuItem,
  RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../api';
import ORDER_STATUS_OPTIONS from '../config/orderStatusOptions';

function getStatusLabel(value) {
  const opt = ORDER_STATUS_OPTIONS.find(o => o.value === value);
  return opt ? opt.label : value;
}

function formatDate(d) {
  if (!d) return null;
  if (typeof d === 'string') return d; // すでに YYYY-MM-DD の場合
  return d.toISOString().slice(0, 10); // Date → YYYY-MM-DD（UTCでも日ズレしない）
}

export default function OrderList() {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  // ★ コピー用
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState(null);
  const [copyMode, setCopyMode] = useState('this'); // this | next | custom
  const [customMonth, setCustomMonth] = useState(''); // YYYY-MM

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const [form, setForm] = useState({
    customerId: '', projectId: '', title: '', status: '',
    amount: '', orderDate: '', deliveryDate: '',
    invoiceDate: '', paymentDueDate: ''
  });

  const loadAll = async () => {
    const [orderRes, customerRes, projectRes] = await Promise.all([
      api.get('/api/orders'),
      api.get('/api/customers'),
      api.get('/api/projects'),
    ]);
    const customersById = Object.fromEntries(customerRes.data.map(c => [String(c.id), c.name]));
    const projectsById = Object.fromEntries(projectRes.data.map(p => [String(p.id), p.title]));

    const fixedOrders = orderRes.data.map(o => ({
      ...o,
      customerName: o.customerName || customersById[String(o.customerId)] || '',
      projectName: o.projectName || projectsById[String(o.projectId)] || '',
    
      orderDate: o.orderDate ? o.orderDate.slice(0,10) : '',
      deliveryDate: o.deliveryDate ? o.deliveryDate.slice(0,10) : '',
      invoiceDate: o.invoiceDate ? o.invoiceDate.slice(0,10) : '',
      paymentDueDate: o.paymentDueDate ? o.paymentDueDate.slice(0,10) : '',
    }));
    
    console.log(fixedOrders);

    setOrders(fixedOrders);
    setCustomers(customerRes.data);
    setProjects(projectRes.data);
  };

  useEffect(() => { loadAll(); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      customerId: Number(form.customerId),
      projectId: form.projectId ? Number(form.projectId) : null,
      amount: form.amount ? Number(form.amount) : null,
      orderDate: form.orderDate || null,
      deliveryDate: form.deliveryDate || null,
      invoiceDate: form.invoiceDate || null,
      paymentDueDate: form.paymentDueDate || null,
    };

    if (editId) {
      await api.put(`/api/orders/${editId}`, payload);
    } else {
      await api.post('/api/orders', payload);
    }

    setOpen(false);
    resetForm();
    loadAll();
  };

  const handleEdit = (row) => {
    setForm({
      customerId: row.customerId,
      projectId: row.projectId,
      title: row.title,
      status: row.status,
      amount: row.amount,
      orderDate: formatDate(row.orderDate),
      deliveryDate: formatDate(row.deliveryDate),
      invoiceDate: formatDate(row.invoiceDate),
      paymentDueDate: formatDate(row.paymentDueDate),
    });
    setEditId(row.id);
    setOpen(true);

    setTimeout(() => {
      const cust = customers.find(c => String(c.id) === String(row.customerId));
      const proj = projects.find(p => String(p.id) === String(row.projectId));
      setSelectedCustomer(cust || null);
      setSelectedProject(proj || null);
    }, 0);
  };

  // ★ コピー開始
  const handleCopyStart = (row) => {
    setCopySourceId(row.id);
    setCopyMode('this');
    setCustomMonth('');
    setCopyOpen(true);
  };

  // ★ コピー実行
  const handleCopyExecute = async () => {
    const src = orders.find(o => o.id === copySourceId);
    if (!src) {
      alert("コピー元が見つかりません");
      return;
    }

    const srcYYYYMM = (src.orderDate || '').slice(0, 7).replace('-', '');
    let targetYYYYMM = '';

    if (copyMode === 'this') {
      targetYYYYMM = srcYYYYMM;
    } else if (copyMode === 'next') {
      const y = Number(srcYYYYMM.slice(0, 4));
      const m = Number(srcYYYYMM.slice(4, 6));
      const next = m === 12
        ? `${y + 1}01`
        : `${y}${String(m + 1).padStart(2, '0')}`;
      targetYYYYMM = next;
    } else if (copyMode === 'custom') {
      if (!customMonth) {
        alert("年月を選択してください");
        return;
      }
      targetYYYYMM = customMonth.replace('-', ''); // YYYY-MM → YYYYMM
    }

    await api.post(`/api/orders/${copySourceId}/copy`, {
      targetMonth: targetYYYYMM
    });

    setCopyOpen(false);
    loadAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    await api.delete(`/api/orders/${id}`);
    loadAll();
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      customerId: '', projectId: '', title: '', status: '',
      amount: '', orderDate: '', deliveryDate: '',
      invoiceDate: '', paymentDueDate: ''
    });
    setSelectedCustomer(null);
    setSelectedProject(null);
  };

  const columns = [
    { field: 'title', headerName: '受注名', width: 180 },
    { field: 'customerName', headerName: '顧客名', width: 160 },
    { field: 'projectName', headerName: '案件名', width: 160 },
    {
      field: 'status',
      headerName: 'ステータス',
      width: 110,
      renderCell: (params) => getStatusLabel(params.row.status)
    },
    {
      field: 'amount',
      headerName: '金額',
      width: 110,
      renderCell: (params) =>
        params.value != null
          ? `¥${Number(params.value).toLocaleString()}`
          : ''
    },
    {
      field: 'orderDate',
      headerName: '受注日',
      width: 110,
      renderCell: (params) => formatDate(params.row.orderDate)
    },
    {
      field: 'deliveryDate',
      headerName: '納品予定',
      width: 110,
      renderCell: (params) => formatDate(params.row.deliveryDate)
    },
    {
      field: 'invoiceDate',
      headerName: '請求日',
      width: 110,
      renderCell: (params) => formatDate(params.row.invoiceDate)
    },
    {
      field: 'paymentDueDate',
      headerName: '入金期限',
      width: 110,
      renderCell: (params) => formatDate(params.row.paymentDueDate)
    },

    // ★ コピーアイコン
    {
      field: 'copy',
      headerName: '',
      width: 60,
      renderCell: (params) => (
        <IconButton onClick={() => handleCopyStart(params.row)}>
          <ContentCopyIcon />
        </IconButton>
      )
    },

    {
      field: 'actions', headerName: '', width: 100,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEdit(params.row)}><EditIcon /></IconButton>
          <IconButton onClick={() => handleDelete(params.row.id)}><DeleteIcon /></IconButton>
        </>
      )
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => { setOpen(true); resetForm(); }}>
          新規受注を追加
        </Button>
      </Box>

      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1300 }}>
          <DataGrid
            rows={orders}
            columns={columns}
            autoHeight
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
          />
        </Box>
      </Box>

      {/* 編集ダイアログ */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? '受注を編集' : '新規受注を登録'}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={customers}
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedCustomer}
            onChange={(e, val) => {
              setSelectedCustomer(val);
              setForm({ ...form, customerId: val ? val.id : '' });
            }}
            renderInput={(params) =>
              <TextField {...params} label="顧客" margin="dense" fullWidth variant="outlined" />}
          />

          <Autocomplete
            options={projects}
            getOptionLabel={(o) => o.title || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedProject}
            onChange={(e, val) => {
              setSelectedProject(val);
              setForm({ ...form, projectId: val ? val.id : '' });
            }}
            renderInput={(params) =>
              <TextField {...params} label="案件" margin="dense" fullWidth variant="outlined" />}
          />

          <TextField fullWidth label="受注名" name="title" margin="dense" value={form.title} onChange={handleChange} variant="outlined" />

          <FormControl fullWidth margin="dense" variant="outlined">
            <InputLabel id="status-label">ステータス</InputLabel>
            <Select
              labelId="status-label"
              label="ステータス"
              name="status"
              value={form.status}
              onChange={e => setForm({ ...form, status: e.target.value })}
            >
              {ORDER_STATUS_OPTIONS.map(opt => (
                <MenuItem value={opt.value} key={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField fullWidth label="金額" name="amount" type="number" margin="dense" value={form.amount} onChange={handleChange} variant="outlined" />

          <TextField fullWidth label="受注日" name="orderDate" type="date" margin="dense" variant="outlined" InputLabelProps={{ shrink: true }} value={form.orderDate} onChange={handleChange} />

          <TextField fullWidth label="納品予定" name="deliveryDate" type="date" margin="dense" variant="outlined" InputLabelProps={{ shrink: true }} value={form.deliveryDate} onChange={handleChange} />

          <TextField fullWidth label="請求日" name="invoiceDate" type="date" margin="dense" variant="outlined" InputLabelProps={{ shrink: true }} value={form.invoiceDate} onChange={handleChange} />

          <TextField fullWidth label="入金期限" name="paymentDueDate" type="date" margin="dense" variant="outlined" InputLabelProps={{ shrink: true }} value={form.paymentDueDate} onChange={handleChange} />

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSubmit}>{editId ? '更新' : '登録'}</Button>
        </DialogActions>
      </Dialog>

      {/* ★ コピー用ダイアログ（完全版） */}
      <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>受注をコピー</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={copyMode}
            onChange={(e) => setCopyMode(e.target.value)}
            sx={{ mt: 1 }}
          >
            <FormControlLabel value="this" control={<Radio />} label="当月分としてコピー" />
            <FormControlLabel value="next" control={<Radio />} label="翌月分としてコピー" />
            <FormControlLabel value="custom" control={<Radio />} label="その他（月を選択）" />
          </RadioGroup>

          {copyMode === 'custom' && (
            <TextField
              fullWidth
              margin="dense"
              type="month"
              label="コピー先の月を選択"
              value={customMonth}
              onChange={(e) => setCustomMonth(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleCopyExecute}>コピー</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
